import React, { useCallback } from 'react';
import {
  Box,
  Card,
  CardMedia,
  Checkbox,
  Divider,
  IconButton,
  ListItemText,
  MenuItem,
  Select,
  TextField,
  Tooltip,
  Typography,
  Paper,
  Stack,
  Chip,
  Avatar,
  Badge,
  Zoom,
  Collapse,
} from '@mui/material';
import {
  DeleteOutline as DeleteIcon,
  AddCircleOutline as AddCircleOutlineIcon,
  RemoveCircleOutline as RemoveCircleOutlineIcon,
  PlayArrow as PlayArrowIcon,
  Share as ShareIcon,
  AttachMoney as AttachMoneyIcon,
  Star as StarIcon,
  TrendingUp as TrendingUpIcon,
  Warning as WarningIcon,
  CheckCircle as CheckCircleIcon,
} from '@mui/icons-material';
import { RateField } from '../../../../Components/RateField';
import {
  platformRatesListFromCreator,
  platformRatesListFromSelectedKeys,
  getRelatedPromotions,
  promotionLabelsFromList,
  USAGE_RIGHTS_OPTIONS,
} from '../../../../Utils/constants';

function calculateBasePrice(creator, videos) {
  return videos.reduce((accum, video) => {
    const promoCost = creator[video.rateKey] || 0;
    const crossCost = Array.from(video.crossPostingKeys || []).reduce(
      (sum, crossKey) => sum + Math.max((creator[crossKey] || 0) / 4, 0),
      0
    );
    return accum + promoCost + crossCost;
  }, 0);
}

function calculateUsageRightsCost(basePrice, usageRightsKey, usageRightsOverride) {
  if (!usageRightsKey) return 0;
  const recommended = basePrice * USAGE_RIGHTS_OPTIONS[usageRightsKey].recommendedPercentage;
  return usageRightsOverride !== undefined ? usageRightsOverride : recommended;
}

function VideoDetails({
  creator,
  video,
  index,
  videoIndex,
  promotions,
  handleOptionChange,
  handleRemoveVideo,
  videoAmount,
  triedNext,
}) {
  const crossPostOptions = platformRatesListFromSelectedKeys(
    creator,
    getRelatedPromotions(video.rateKey)
  );

  return (
    <Zoom in timeout={300}>
      <Paper 
        elevation={0}
        sx={{ 
          p: 3,
          mb: 2,
          borderRadius: 3,
          background: 'linear-gradient(135deg, rgba(103, 126, 234, 0.05) 0%, rgba(118, 75, 162, 0.05) 100%)',
          border: '1px solid rgba(103, 126, 234, 0.15)',
          position: 'relative',
          transition: 'all 0.3s ease',
          '&:hover': {
            transform: 'translateY(-2px)',
            boxShadow: '0 10px 30px rgba(103, 126, 234, 0.1)',
            background: 'linear-gradient(135deg, rgba(103, 126, 234, 0.08) 0%, rgba(118, 75, 162, 0.08) 100%)',
          }
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
          <Tooltip title={videoAmount <= 1 ? "Can't remove the last video" : "Remove this video"}>
            <span>
              <IconButton
                disabled={videoAmount <= 1}
                onClick={() => handleRemoveVideo(index, videoIndex)}
                sx={{
                  color: videoAmount <= 1 ? 'text.disabled' : 'error.main',
                  backgroundColor: videoAmount <= 1 ? 'transparent' : 'rgba(244, 67, 54, 0.1)',
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    backgroundColor: videoAmount <= 1 ? 'transparent' : 'rgba(244, 67, 54, 0.2)',
                    transform: 'scale(1.1)'
                  },
                  '&:disabled': {
                    opacity: 0.3
                  }
                }}
              >
                <RemoveCircleOutlineIcon />
              </IconButton>
            </span>
          </Tooltip>

          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Stack spacing={2}>
              <TextField
                value={video.rateKey}
                onChange={(e) =>
                  handleOptionChange(index, 'videos', e.target.value, 'rateKey', videoIndex)
                }
                label="Promotion Type"
                select
                fullWidth
                variant="outlined"
                size="small"
                error={triedNext && !video.rateKey}
                helperText={
                  triedNext && !video.rateKey
                    ? 'Select a promotion for this video'
                    : ''
                }
                InputProps={{
                  startAdornment: <PlayArrowIcon sx={{ mr: 1, color: '#667eea' }} />,
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    backgroundColor: 'white',
                    transition: 'all 0.2s ease',
                    '&:hover': {
                      backgroundColor: 'rgba(103, 126, 234, 0.02)',
                    },
                    '&.Mui-focused': {
                      backgroundColor: 'rgba(103, 126, 234, 0.05)',
                    }
                  }
                }}
              >
                {promotions.map((promo) => (
                  <MenuItem
                    key={`${creator.creator}-${video.id}-${promo.rateKey}`}
                    value={promo.rateKey}
                  >
                    <Chip 
                      label={promo.label} 
                      size="small" 
                      variant="outlined"
                      color="primary"
                    />
                  </MenuItem>
                ))}
              </TextField>

              <TextField
                label="Crossposting"
                select
                fullWidth
                variant="outlined"
                size="small"
                InputProps={{
                  startAdornment: <ShareIcon sx={{ mr: 1, color: '#667eea' }} />,
                }}
                SelectProps={{
                  multiple: true,
                  renderValue: (selected) => (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {promotionLabelsFromList(selected).map((label) => (
                        <Chip 
                          key={label} 
                          label={label} 
                          size="small"
                          color="secondary"
                          variant="filled"
                        />
                      ))}
                    </Box>
                  ),
                }}
                value={[...video.crossPostingKeys]}
                onChange={(e) =>
                  handleOptionChange(
                    index,
                    'videos',
                    e.target.value,
                    'crossPostingKeys',
                    videoIndex
                  )
                }
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    backgroundColor: 'white',
                    transition: 'all 0.2s ease',
                    '&:hover': {
                      backgroundColor: 'rgba(103, 126, 234, 0.02)',
                    },
                    '&.Mui-focused': {
                      backgroundColor: 'rgba(103, 126, 234, 0.05)',
                    }
                  }
                }}
              >
                {crossPostOptions.map((promo) => (
                  <MenuItem
                    key={`${creator.creator}-${video.id}-cross-${promo.rateKey}`}
                    value={promo.rateKey}
                  >
                    <Checkbox 
                      checked={video.crossPostingKeys.has(promo.rateKey)}
                      sx={{ color: '#667eea' }}
                    />
                    <ListItemText
                      primary={promo.label}
                      primaryTypographyProps={{ fontWeight: 500 }}
                    />
                  </MenuItem>
                ))}
              </TextField>
            </Stack>
          </Box>
        </Box>
      </Paper>
    </Zoom>
  );
}

const hasInvalidRateKey = (videos) =>
  videos.some((video) => !video.rateKey || video.rateKey.trim() === '');

export default function CreatorDetailsCard({
  creator,
  index,
  handlePriceChange,
  handleOptionChange,
  onRemove,
  triedNext,
  handleAddVideo,
  handleRemoveVideo,
  setCreatorsData,
}) {
  const promotions = platformRatesListFromCreator(creator);
  const hasEmptyKey = hasInvalidRateKey(creator.videos);
  const minAllowed = (creator.originalBasePrice || 0) * 0.7;
  const isBelowMin = creator.basePrice < minAllowed;

  const handleRateEdit = useCallback(
    (rateKey, newVal, isCrosspost = false) => {
      const numericCost = Math.max(0, parseFloat(newVal) || 0);
      setCreatorsData(prev => {
        const next = [...prev];
        const c = { ...next[index] };
        
        if (isCrosspost) {
          c[rateKey] = numericCost * 4;
        } else {
          c[rateKey] = numericCost;
        }
        
        const newBasePrice = calculateBasePrice(c, c.videos);
        c.basePrice = newBasePrice;
        c.adjustedPrice = newBasePrice + calculateUsageRightsCost(
          newBasePrice,
          c.usageRightsKey,
          c.usageRightsOverride
        );
        
        next[index] = c;
        return next;
      });
    },
    [index, setCreatorsData]
  );

  const handleUsageRightsChange = (key) => {
    setCreatorsData(prev => {
      const next = [...prev];
      const c = { ...next[index] };
      c.usageRightsKey = key;
      c.usageRightsOverride = undefined;
      c.adjustedPrice = c.basePrice + calculateUsageRightsCost(c.basePrice, key);
      next[index] = c;
      return next;
    });
  };

  const handleUsageRightsOverride = (value) => {
    const numericValue = Math.max(0, parseFloat(value) || 0);
    setCreatorsData(prev => {
      const next = [...prev];
      const c = { ...next[index] };
      c.usageRightsOverride = numericValue;
      c.adjustedPrice = c.basePrice + numericValue;
      next[index] = c;
      return next;
    });
  };

  const handleBasePriceOverride = (value) => {
    const numericPrice = Math.max(0, parseFloat(value) || 0);
    setCreatorsData(prev => {
      const next = [...prev];
      const c = { ...next[index] };
      c.basePrice = numericPrice;
      
      if (c.videos.length > 0 && c.videos[0].rateKey) {
        const firstVideoCost = numericPrice - calculateBasePrice(c, c.videos.slice(1));
        c[c.videos[0].rateKey] = Math.max(0, firstVideoCost);
      }
      
      c.adjustedPrice = numericPrice + calculateUsageRightsCost(
        numericPrice,
        c.usageRightsKey,
        c.usageRightsOverride
      );
      next[index] = c;
      return next;
    });
  };

  const mainItems = creator.videos.map((video) => ({
    rateKey: video.rateKey,
    label: promotionLabelsFromList([video.rateKey]),
    cost: creator[video.rateKey] || 0,
    editable: video === creator.videos[0] ? false : true
  }));

  const crossItems = [];
  creator.videos.forEach((video) => {
    video.crossPostingKeys.forEach((crossKey) => {
      const totalCrossCost = creator[crossKey] || 0;
      crossItems.push({
        crossKey,
        label: promotionLabelsFromList([crossKey]),
        cost: totalCrossCost / 4,
      });
    });
  });

  const usageRightsCost = calculateUsageRightsCost(
    creator.basePrice,
    creator.usageRightsKey,
    creator.usageRightsOverride
  );
  const recommendedUsageCost = creator.usageRightsKey 
    ? creator.basePrice * USAGE_RIGHTS_OPTIONS[creator.usageRightsKey].recommendedPercentage 
    : 0;
  const isUsageBelowRecommended = usageRightsCost < recommendedUsageCost * 0.7;

  return (
    <Card 
      sx={{ 
        borderRadius: 4,
        background: 'rgba(255, 255, 255, 0.95)',
        backdropFilter: 'blur(20px)',
        border: '1px solid rgba(255, 255, 255, 0.2)',
        boxShadow: '0 20px 40px rgba(0,0,0,0.08)',
        overflow: 'visible',
        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        position: 'relative',
        '&:hover': {
          transform: 'translateY(-8px)',
          boxShadow: '0 32px 64px rgba(0,0,0,0.12)',
        },
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: '4px',
          background: 'linear-gradient(90deg, #667eea 0%, #764ba2 100%)',
          borderRadius: '16px 16px 0 0'
        }
      }}
    >
      <Box sx={{ p: 3 }}>
        {/* Header Section */}
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 3, mb: 3 }}>
          <Box sx={{ position: 'relative' }}>
            <Badge
              overlap="circular"
              anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
              badgeContent={
                <Avatar 
                  sx={{ 
                    width: 24, 
                    height: 24, 
                    bgcolor: '#4CAF50',
                    fontSize: '12px',
                    border: '2px solid white'
                  }}
                >
                  <CheckCircleIcon sx={{ fontSize: 14 }} />
                </Avatar>
              }
            >
              <Avatar
                src={creator.pfphref || '/default_profile.png'}
                alt={creator.creator}
                sx={{
                  width: { xs: 80, md: 100 },
                  height: { xs: 80, md: 100 },
                  border: '4px solid rgba(103, 126, 234, 0.2)',
                  boxShadow: '0 8px 24px rgba(103, 126, 234, 0.2)'
                }}
              />
            </Badge>
          </Box>

          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Stack spacing={2}>
              <Box>
                <Typography
                  variant="h5"
                  sx={{ 
                    fontWeight: 700,
                    color: '#2c3e50',
                    textTransform: 'capitalize',
                    mb: 0.5
                  }}
                >
                  {creator.display_name}
                </Typography>
                
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                  <Chip
                    icon={<PlayArrowIcon />}
                    label={`${creator.videos.length || 0} Video${creator.videos.length === 1 ? '' : 's'}`}
                    size="small"
                    color="primary"
                    variant="outlined"
                  />
                  <Chip
                    icon={<AttachMoneyIcon />}
                    label={`$${creator.adjustedPrice.toFixed(2)}`}
                    size="small"
                    color="success"
                    variant="filled"
                    sx={{ fontWeight: 600 }}
                  />
                </Box>
              </Box>

              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Tooltip
                  title={
                    hasEmptyKey
                      ? 'Finish filling the current video(s) first'
                      : 'Add another video'
                  }
                >
                  <span>
                    <IconButton
                      disabled={hasEmptyKey}
                      onClick={() => !hasEmptyKey && handleAddVideo(index)}
                      sx={{
                        backgroundColor: hasEmptyKey ? 'rgba(0,0,0,0.04)' : 'rgba(103, 126, 234, 0.1)',
                        color: hasEmptyKey ? 'text.disabled' : '#667eea',
                        transition: 'all 0.2s ease',
                        '&:hover': {
                          backgroundColor: hasEmptyKey ? 'rgba(0,0,0,0.04)' : 'rgba(103, 126, 234, 0.2)',
                          transform: hasEmptyKey ? 'none' : 'scale(1.1)'
                        },
                        '&:disabled': {
                          opacity: 0.3
                        }
                      }}
                    >
                      <AddCircleOutlineIcon />
                    </IconButton>
                  </span>
                </Tooltip>
                
                <Typography variant="body2" color="text.secondary">
                  Add Video
                </Typography>
              </Box>
            </Stack>
          </Box>

          <Tooltip title="Remove Creator">
            <IconButton 
              onClick={() => onRemove(creator.creator)}
              sx={{
                color: 'error.main',
                backgroundColor: 'rgba(244, 67, 54, 0.1)',
                transition: 'all 0.2s ease',
                '&:hover': {
                  backgroundColor: 'rgba(244, 67, 54, 0.2)',
                  transform: 'scale(1.1)'
                }
              }}
            >
              <DeleteIcon />
            </IconButton>
          </Tooltip>
        </Box>

        <Divider sx={{ mb: 3, opacity: 0.1 }} />

        {/* Videos Section */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, color: '#2c3e50' }}>
            Video Configuration
          </Typography>
          {creator.videos.map((video, videoIndex) => (
            <VideoDetails
              key={`${creator.creator}-videos-${video.id}`}
              video={video}
              creator={creator}
              promotions={promotions}
              handleOptionChange={handleOptionChange}
              handleRemoveVideo={handleRemoveVideo}
              triedNext={triedNext}
              index={index}
              videoIndex={videoIndex}
              videoAmount={creator.videos.length}
            />
          ))}
        </Box>

        {/* Pricing Section */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, color: '#2c3e50' }}>
            Pricing Configuration
          </Typography>
          
          <RateField
            error={triedNext && (!creator.basePrice || creator.basePrice === 0)}
            helperText={
              isBelowMin ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'warning.main' }}>
                  <WarningIcon sx={{ fontSize: 16 }} />
                  <Typography variant="caption" color="warning.main">
                    Below recommended minimum of ${minAllowed.toFixed(2)} (70% of original)
                  </Typography>
                </Box>
              ) : triedNext && (!creator.basePrice || creator.basePrice === 0) ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'error.main' }}>
                  <WarningIcon sx={{ fontSize: 16 }} />
                  <Typography variant="caption" color="error.main">
                    Enter a rate for this creator
                  </Typography>
                </Box>
              ) : (
                ''
              )
            }
            label="Base Price Override"
            variant="outlined"
            fullWidth
            size="small"
            value={creator.basePrice}
            onChange={handleBasePriceOverride}
            InputProps={{
              startAdornment: <AttachMoneyIcon sx={{ mr: 1, color: '#667eea' }} />,
            }}
            sx={{ 
              mb: 3,
              '& .MuiOutlinedInput-root': {
                borderRadius: 2,
                backgroundColor: 'white',
                transition: 'all 0.2s ease',
                '&:hover': {
                  backgroundColor: 'rgba(103, 126, 234, 0.02)',
                },
                '&.Mui-focused': {
                  backgroundColor: 'rgba(103, 126, 234, 0.05)',
                }
              }
            }}
          />
        </Box>

        {/* Cost Breakdown */}
        <Card 
          elevation={0}
          sx={{ 
            mb: 3,
            borderRadius: 3,
            background: 'linear-gradient(135deg, #f8faff 0%, #f1f5ff 100%)',
            border: '1px solid rgba(103, 126, 234, 0.1)'
          }}
        >
          <Box sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
              <TrendingUpIcon sx={{ color: '#667eea' }} />
              <Typography variant="h6" sx={{ fontWeight: 600, color: '#2c3e50' }}>
                Cost Breakdown
              </Typography>
            </Box>
            
            <Stack spacing={2}>
              {mainItems.map((item, idx) => item.rateKey && (
                <Box key={`mainpromo-${idx}`} sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Box sx={{ flex: 1 }}>
                    <Chip 
                      label={item.label} 
                      size="small" 
                      color="primary" 
                      variant="outlined"
                    />
                  </Box>
                  <TextField
                    variant="outlined"
                    size="small"
                    type="number"
                    value={item.cost.toFixed(2)}
                    onChange={(e) => handleRateEdit(item.rateKey, e.target.value, false)}
                    inputProps={{ min: 0 }}
                    disabled={!item.editable}
                    sx={{
                      width: 120,
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 2,
                        backgroundColor: item.editable ? 'white' : 'rgba(0,0,0,0.04)',
                      }
                    }}
                  />
                </Box>
              ))}

              {crossItems.length > 0 && (
                <>
                  <Divider sx={{ my: 1, opacity: 0.3 }} />
                  {crossItems.map((x, i) => (
                    <Box key={`crossitem-${i}`} sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <Box sx={{ flex: 1 }}>
                        <Chip 
                          label={`Crosspost: ${x.label}`} 
                          size="small" 
                          color="secondary" 
                          variant="filled"
                          icon={<ShareIcon />}
                        />
                      </Box>
                      <TextField
                        variant="outlined"
                        size="small"
                        type="number"
                        value={x.cost.toFixed(2)}
                        onChange={(e) => handleRateEdit(x.crossKey, e.target.value, true)}
                        inputProps={{ min: 0 }}
                        sx={{
                          width: 120,
                          '& .MuiOutlinedInput-root': {
                            borderRadius: 2,
                            backgroundColor: 'white',
                          }
                        }}
                      />
                    </Box>
                  ))}
                </>
              )}

              <Divider sx={{ my: 1, opacity: 0.3 }} />
              
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="body2" sx={{ fontWeight: 500, color: '#2c3e50' }}>
                    Usage Rights
                  </Typography>
                </Box>
                <Select
                  value={creator.usageRightsKey || ''}
                  onChange={(e) => handleUsageRightsChange(e.target.value)}
                  sx={{ 
                    width: 120, 
                    height: 40,
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                    }
                  }}
                  size="small"
                >
                  <MenuItem value="">None</MenuItem>
                  {Object.entries(USAGE_RIGHTS_OPTIONS).map(([key, { label }]) => (
                    <MenuItem key={key} value={key}>
                      {label}
                    </MenuItem>
                  ))}
                </Select>
                {creator.usageRightsKey && (
                  <TextField
                    variant="outlined"
                    size="small"
                    type="number"
                    sx={{ 
                      width: 120,
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 2,
                        backgroundColor: 'white',
                      }
                    }}
                    value={usageRightsCost.toFixed(2)}
                    onChange={(e) => handleUsageRightsOverride(e.target.value)}
                    inputProps={{ min: 0 }}
                    error={isUsageBelowRecommended}
                    helperText={isUsageBelowRecommended ? `Rec: $${recommendedUsageCost.toFixed(2)}` : ''}
                  />
                )}
              </Box>
            </Stack>
          </Box>
        </Card>

        {/* Final Price Display */}
        <Paper 
          elevation={0}
          sx={{ 
            p: 3,
            borderRadius: 3,
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            color: 'white',
            textAlign: 'center'
          }}
        >
          <Stack direction="row" alignItems="center" justifyContent="center" spacing={2}>
            <StarIcon sx={{ fontSize: 28 }} />
            <Box>
              <Typography variant="body2" sx={{ opacity: 0.9 }}>
                Total Adjusted Price
              </Typography>
              <Typography 
                variant="h4" 
                sx={{ 
                  fontWeight: 800,
                  textShadow: '0 2px 4px rgba(0,0,0,0.2)'
                }}
              >
                ${creator.adjustedPrice.toFixed(2)}
              </Typography>
            </Box>
            <StarIcon sx={{ fontSize: 28 }} />
          </Stack>
        </Paper>
      </Box>
    </Card>
  );
}